/**
 * career-values-interview — the qualitative Werte-Interview (W1 redesign §4–§5,
 * V3/V4), route `/workshops/values/interview`.
 *
 * Phase 2 (Runden-Planner) + Phase 3a (adaptive interview) that continue AFTER the
 * Werte-Kartenspiel: the planner reads the committed pyramid and plans 2–3 rounds of
 * question blocks; the interview walks them through the existing probe/fold path,
 * one adaptive question at a time. HITL: the answer is composed on screen and only
 * folded on »Bestätigen« (answer-before-fold). The evidence is model-anchored
 * (Schwartz + Big-Five) and the coverage is shown as honest »Signale« with two-tier
 * labels (»noch offen« / »vorläufig« / »gut gestützt«). Candidate-facing, elicited
 * material is »Signale«, NEVER »Belege«.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
  type Urn,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import {
  semanticDifferential,
  readDifferentialValue,
  openTextField,
  readOpenText,
  renderCardInputs,
  isCardDegraded,
  isAnswerable,
  emptySelection,
  type AnswerCard,
  type AnswerSelection,
} from "../lib/elicitation-card"
import type { ElicitationCard, RoundPlan, ValuePyramid } from "@career/definition-v2"
import { RouterToken, I18nToken, apiGet, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"

// ─── The interview view the api returns (contract `InterviewView`) ──────────────

interface DimensionReadout {
  framework: "schwartz" | "big_five"
  dimension: string
  coverage: number
  confidence: number
  label: "noch offen" | "vorläufig" | "gut gestützt"
}
interface Turn {
  answer: { kind: string; text?: string; choice?: string; [k: string]: unknown }
  occurredAt: string
}
interface BlockView {
  block: {
    id: string
    title: string
    primitive: ElicitationCard["primitive"]
    construct: string
    targetCardIds: string[]
    framework: "schwartz" | "big_five"
    dimension: string
  }
  round: number
  roundTitle: string
  gate: "locked" | "available" | "in_progress" | "accepted"
  card: ElicitationCard | null
  skipped: boolean
  rationale: string | null
  denkweise: string | null
  turns: Turn[]
  evidence: { summary: string }[]
  producedClaimRefs: string[]
}
interface RoundView {
  round: number
  title: string
  blocks: BlockView[]
}
interface InterviewView {
  plan: RoundPlan | null
  pyramid: ValuePyramid | null
  rounds: RoundView[]
  readouts: unknown[]
  labels: DimensionReadout[]
  complete: boolean
}

/** German dimension labels (mirrors value-model's SCHWARTZ/BIG_FIVE label maps). */
const DIMENSION_LABEL_DE: Record<string, string> = {
  conservation: "Bewahren",
  self_transcendence: "Über sich hinaus",
  openness_to_change: "Offenheit für Neues",
  self_enhancement: "Vorankommen",
  openness: "Offenheit",
  conscientiousness: "Gewissenhaftigkeit",
  extraversion: "Extraversion",
  agreeableness: "Verträglichkeit",
  emotional_stability: "Emotionale Stabilität",
}

const LABEL_CLASS: Record<DimensionReadout["label"], string> = {
  "noch offen": "is-open",
  "vorläufig": "is-tentative",
  "gut gestützt": "is-strong",
}

export class ValuesInterviewVm implements ViewModel {
  readonly view: Signal<InterviewView | null> = signal<InterviewView | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /** A per-block confirm (or the plan) is in flight. */
  readonly busy: Signal<boolean> = signal(false)
  /** A whole round's stimuli are generating — the honest »thinking« state between rounds. */
  readonly roundGen: Signal<boolean> = signal(false)
  /** In-progress selections, keyed by block id (a round shows several cards at once). Free-text
   *  and slider values are read from the DOM on submit (no per-keystroke re-render). */
  readonly selections: Signal<Record<string, AnswerSelection>> = signal<Record<string, AnswerSelection>>({})
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(private readonly router: Router, i18n: CareerI18n) {
    this.t = i18n.t
    this.renderDeps = [this.view, this.loaded, this.error, this.busy, this.roundGen, this.selections]
  }

  attach(): () => void {
    void this.refresh().then(() => this.maybeGenerateRound())
    return () => {}
  }

  selFor(blockId: string): AnswerSelection {
    return this.selections.value[blockId] ?? emptySelection()
  }
  private setSel(blockId: string, sel: AnswerSelection): void {
    this.selections.set({ ...this.selections.value, [blockId]: sel })
  }
  private clearSel(blockId: string): void {
    const next = { ...this.selections.value }
    delete next[blockId]
    this.selections.set(next)
  }

  pickOption(blockId: string, role: "choice" | "best" | "worst", id: string): void {
    const s = this.selFor(blockId)
    if (role === "choice") this.setSel(blockId, { ...s, choiceId: s.choiceId === id ? null : id })
    else if (role === "best") this.setSel(blockId, { ...s, bestId: s.bestId === id ? null : id, worstId: s.worstId === id ? null : s.worstId })
    else this.setSel(blockId, { ...s, worstId: s.worstId === id ? null : id, bestId: s.bestId === id ? null : s.bestId })
  }
  toggleVeto(blockId: string, id: string): void {
    const s = this.selFor(blockId)
    const vetoes = s.vetoes.includes(id) ? s.vetoes.filter((v) => v !== id) : [...s.vetoes, id]
    this.setSel(blockId, { ...s, vetoes })
  }

  private async refresh(): Promise<void> {
    try {
      this.view.set(await apiGet<InterviewView>("/api/workshops/values/interview"))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  allBlocks(): BlockView[] {
    return this.view.value?.rounds.flatMap((r) => r.blocks) ?? []
  }

  /** The active round — the first round holding a non-accepted block (null when complete). */
  currentRound(): RoundView | null {
    return this.view.value?.rounds.find((r) => r.blocks.some((b) => b.gate !== "accepted")) ?? null
  }

  /** A round no block of which is generated yet (freshly reached → auto-generate the set). */
  private roundUngenerated(round: RoundView): boolean {
    return round.blocks.every((b) => !b.card && b.gate !== "accepted" && !b.skipped)
  }

  async plan(): Promise<void> {
    if (this.busy.value) return
    this.busy.set(true)
    this.error.set(null)
    try {
      this.view.set(await apiPost<InterviewView>("/api/workshops/values/interview/plan"))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(false)
    }
    this.maybeGenerateRound()
  }

  /** Generate a whole round's stimuli in ONE call (parallel probe fan-out server-side). */
  async generateRound(round?: number): Promise<void> {
    if (this.roundGen.value || this.busy.value) return
    this.roundGen.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<{ interview: InterviewView }>(
        "/api/workshops/values/interview/round",
        round === undefined ? {} : { round },
      )
      this.view.set(res.interview)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.roundGen.set(false)
    }
  }

  /** Auto-advance: when the active round is freshly reached (no cards yet), generate the set. */
  maybeGenerateRound(): void {
    const round = this.currentRound()
    if (round && this.roundUngenerated(round) && !this.roundGen.value && !this.busy.value) {
      void this.generateRound(round.round)
    }
  }

  /** Retry ONE block whose probe failed (left card-less inside an already-generated round). */
  async retryBlock(blockId: string): Promise<void> {
    if (this.busy.value || this.roundGen.value) return
    this.roundGen.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<{ interview: InterviewView }>("/api/workshops/values/interview/stimulus", { blockId })
      this.view.set(res.interview)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.roundGen.set(false)
    }
  }

  async submit(blockId: string, answer: Record<string, unknown>): Promise<void> {
    if (this.busy.value) return
    this.busy.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<{ interview: InterviewView }>("/api/workshops/values/interview/turn", {
        blockId,
        answer: { ...answer, cardId: blockId },
      })
      this.view.set(res.interview)
      this.clearSel(blockId)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(false)
    }
    this.maybeGenerateRound() // the round's last block answered → generate the next set
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerValuesInterview extends KitElement<ValuesInterviewVm> {
  protected createViewModel(ctx: KitContext): ValuesInterviewVm {
    return new ValuesInterviewVm(ctx.inject(RouterToken), ctx.inject(I18nToken))
  }

  // ── A ROUND of cards (batch): the current round shows every question at once;
  //    each block confirms independently, then the next round auto-generates. ──

  /** The answer inputs + confirm for ONE block, with its OWN selection (keyed by block id). */
  private answerArea(vm: ValuesInterviewVm, block: BlockView, card: ElicitationCard): TemplateResult {
    const id = block.block.id
    // No answerable reaction grid (degraded / card_sort / a free-text primitive) → open text.
    if (isCardDegraded(card) || card.primitive === "card_sort" || card.primitive === "why_ladder" || card.primitive === "open_narrative") {
      return this.openBody(vm, id)
    }
    const sel = vm.selFor(id)
    const labels = {
      choose: vm.t("interview.card.choose"),
      best: vm.t("interview.card.best"),
      worst: vm.t("interview.card.worst"),
      veto: vm.t("interview.card.veto"),
      openPlaceholder: vm.t("interview.card.openPlaceholder"),
    }
    const inputs = renderCardInputs(card as AnswerCard, { id, sel, submitting: vm.busy.value, labels })
    return html`${inputs}${this.submitButton(vm, isAnswerable(card.primitive, sel), id)}`
  }

  /** why_ladder / open_narrative — the shared seamless open-text control (box-less,
   *  underline-only, auto-growing) folded as an open answer. Read on submit from the DOM. */
  private openBody(vm: ValuesInterviewVm, blockId: string): TemplateResult {
    return html`
      <div class="iv-open">
        ${openTextField(vm.t("interview.card.openPlaceholder"), vm.t("interview.card.openPlaceholder"))}
        ${this.submitButton(vm, true, blockId)}
      </div>`
  }

  private submitButton(vm: ValuesInterviewVm, ready: boolean, blockId: string): TemplateResult {
    const submitting = vm.busy.value
    return html`<button
      class="primary iv-submit"
      data-act="submit-answer" data-id=${blockId}
      ?disabled=${submitting || !ready}
      lang="de"
    >${submitting ? vm.t("interview.card.submitting") : vm.t("interview.card.submit")}</button>`
  }

  /** A live (unanswered) card in the round — prose header (interview presentation) + the
   *  shared answer controls. `@keydown` carries the block id so Enter confirms THIS card. */
  private liveCard(vm: ValuesInterviewVm, block: BlockView): TemplateResult {
    const card = block.card!
    const id = block.block.id
    return html`<div class="iv-card" data-block=${id} @keydown=${(e: KeyboardEvent) => this.onCardKeydown(vm, e, id)}>
      ${block.rationale ? html`<p class="iv-rationale" lang="de">${block.rationale}</p>` : nothing}
      ${card.eyebrow ? html`<p class="iv-card-eyebrow eyebrow" lang="de">${card.eyebrow}</p>` : nothing}
      ${card.scenario ? html`<p class="iv-scenario" lang="de">${card.scenario}</p>` : nothing}
      <p class="iv-prompt" lang="de">${card.prompt}</p>
      ${card.sublegend ? html`<p class="iv-sublegend surface-empty" lang="de">${card.sublegend}</p>` : nothing}
      ${this.answerArea(vm, block, card)}
      ${block.denkweise ? html`<details class="iv-denkweise"><summary lang="de">${vm.t("interview.card.why")}</summary><p lang="de">${block.denkweise}</p></details>` : nothing}
    </div>`
  }

  /** An answered card in the round — collapsed to its folded »Signal« (never a »Beleg«). */
  private foldedCard(vm: ValuesInterviewVm, block: BlockView): TemplateResult {
    const summary = block.evidence[0]?.summary ?? (block.skipped ? vm.t("interview.block.skipped") : "")
    return html`<div class="iv-card is-folded">
      <p class="iv-folded-q" lang="de">${block.card?.prompt ?? block.block.title}</p>
      ${summary ? html`<p class="iv-folded-signal" lang="de">${summary}</p>` : nothing}
    </div>`
  }

  /** A block whose probe failed (card-less within an already-generated round) — a retry. */
  private failedCard(vm: ValuesInterviewVm, block: BlockView): TemplateResult {
    return html`<div class="iv-card is-failed" role="status">
      <p class="iv-prompt" lang="de">${block.block.title}</p>
      <p class="surface-empty" lang="de">${vm.t("interview.round.failed")}</p>
      <button class=${classMap({ primary: true, "is-pending": vm.roundGen.value })} data-act="retry-block" data-id=${block.block.id} ?disabled=${vm.roundGen.value} lang="de">
        ${vm.roundGen.value ? vm.t("interview.card.generating") : vm.t("interview.card.retry")}
      </button>
    </div>`
  }

  /** The honest »thinking« banner shown while a round's questions are being assembled. */
  private thinkingBanner(vm: ValuesInterviewVm, anyAccepted: boolean): TemplateResult {
    return html`<div class="iv-thinking" role="status" aria-live="polite">
      <span class="iv-thinking-dots" aria-hidden="true"><span></span><span></span><span></span></span>
      <span lang="de">${vm.t(anyAccepted ? "interview.round.thinking" : "interview.round.preparing")}</span>
    </div>`
  }

  /** The active round — the whole set of questions at once (or the thinking state while it forms). */
  private roundSection(vm: ValuesInterviewVm, round: RoundView, anyAccepted: boolean): TemplateResult {
    const ungenerated = round.blocks.every((b) => !b.card && b.gate !== "accepted" && !b.skipped)
    return html`<section class="iv-round">
      <p class="eyebrow iv-round-title" lang="de">${round.title}</p>
      ${ungenerated
        ? this.thinkingBanner(vm, anyAccepted)
        : html`<div class="iv-round-cards">
            ${round.blocks.map((b) =>
              b.gate === "accepted" ? this.foldedCard(vm, b) : b.card ? this.liveCard(vm, b) : this.failedCard(vm, b),
            )}
          </div>`}
    </section>`
  }

  /** A finished block's one-line folded read (a »Signal«, never a »Beleg«). */
  private pastBlock(vm: ValuesInterviewVm, block: BlockView): TemplateResult {
    const summary = block.evidence[0]?.summary ?? (block.skipped ? vm.t("interview.block.skipped") : "")
    return html`<li class="iv-past">
      <span class="iv-past-title" lang="de">${block.block.title}</span>
      ${summary ? html`<span class="iv-past-summary" lang="de">${summary}</span>` : nothing}
    </li>`
  }

  /** Completed rounds fold into a compact history below the active round. */
  private pastRoundsSection(vm: ValuesInterviewVm, rounds: RoundView[]): TemplateResult {
    return html`<section class="iv-history">
      <h2 class="surface-section-title" lang="de">${vm.t("interview.history.title")}</h2>
      ${rounds.map(
        (r) => html`<div class="iv-history-round">
          <p class="eyebrow" lang="de">${r.title}</p>
          <ul class="iv-past-list" role="list">${r.blocks.map((b) => this.pastBlock(vm, b))}</ul>
        </div>`,
      )}
    </section>`
  }

  // ── The Signale coverage readout (§8, two-tier honest labels) ─────────────────

  private readoutPanel(vm: ValuesInterviewVm, labels: DimensionReadout[]): TemplateResult {
    const group = (framework: "schwartz" | "big_five") => labels.filter((l) => l.framework === framework)
    const row = (l: DimensionReadout) => html`
      <li class="iv-dim">
        <span class="iv-dim-label" lang="de">${DIMENSION_LABEL_DE[l.dimension] ?? l.dimension}</span>
        <span class="iv-dim-bar" role="presentation"><span class="iv-dim-fill" style=${`--iv-cov:${l.coverage}`}></span></span>
        <span class=${classMap({ chip: true, "iv-dim-label-chip": true, [LABEL_CLASS[l.label]]: true })} lang="de">${l.label}</span>
      </li>`
    const schwartz = group("schwartz")
    const bigFive = group("big_five")
    return html`
      <aside class="iv-readout">
        <h2 class="surface-section-title" lang="de">${vm.t("interview.readout.title")}</h2>
        <p class="surface-empty" lang="de">${vm.t("interview.readout.hint")}</p>
        <p class="eyebrow" lang="de">${vm.t("interview.readout.values")}</p>
        <ul class="iv-dims" role="list">${schwartz.map(row)}</ul>
        <p class="eyebrow" lang="de">${vm.t("interview.readout.workstyle")}</p>
        <ul class="iv-dims" role="list">${bigFive.map(row)}</ul>
      </aside>`
  }

  // ── States ───────────────────────────────────────────────────────────────────

  private noPyramid(vm: ValuesInterviewVm): TemplateResult {
    return html`<div class="iv-intro">
      <p class="surface-lead" lang="de">${vm.t("interview.intro.needPyramid")}</p>
      <button class="primary" data-act="go-cardgame" lang="de">${vm.t("interview.intro.toCardgame")}</button>
    </div>`
  }

  private readyToPlan(vm: ValuesInterviewVm): TemplateResult {
    return html`<div class="iv-intro">
      <p class="surface-lead" lang="de">${vm.t("interview.intro.lede")}</p>
      <button class=${classMap({ primary: true, "is-pending": vm.busy.value })} data-act="plan" ?disabled=${vm.busy.value} lang="de">
        ${vm.busy.value ? vm.t("interview.intro.planning") : vm.t("interview.intro.plan")}
      </button>
    </div>`
  }

  private body(vm: ValuesInterviewVm, view: InterviewView): TemplateResult {
    if (!view.pyramid) return this.noPyramid(vm)
    if (!view.plan) return this.readyToPlan(vm)

    const current = vm.currentRound()
    const anyAccepted = view.rounds.some((r) => r.blocks.some((b) => b.gate === "accepted"))
    // Completed rounds ABOVE the active one fold into history (the active round stays in focus).
    const pastRounds = view.rounds.filter((r) => r !== current && r.blocks.some((b) => b.gate === "accepted"))
    return html`
      <div class="iv-layout">
        <div class="iv-main">
          ${current
            ? this.roundSection(vm, current, anyAccepted)
            : html`<div class="iv-done">
                <h2 class="surface-section-title" lang="de">${vm.t("interview.done.title")}</h2>
                <p class="surface-lead" lang="de">${vm.t("interview.done.hint")}</p>
                <button class="primary" data-act="go-sektion" lang="de">${vm.t("interview.done.toSektion")}</button>
              </div>`}
          ${pastRounds.length > 0 ? this.pastRoundsSection(vm, pastRounds) : nothing}
        </div>
        ${this.readoutPanel(vm, view.labels)}
      </div>`
  }

  protected render(vm: ValuesInterviewVm): TemplateResult {
    const rootUrn = (this.urn ?? "urn:career:workshop:values") as Urn
    const err = vm.error.value
    const view = vm.view.value
    return html`
      <section class="surface values-interview" urn=${rootUrn} aria-labelledby="iv-title">
        <p class="eyebrow" lang="de">${vm.t("interview.kicker")}</p>
        <h1 id="iv-title" class="surface-title" lang="de">${vm.t("surface.valuesInterview.title")}</h1>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
        ${vm.loaded.value && view ? this.body(vm, view) : nothing}
      </section>`
  }

  protected override actions(vm: ValuesInterviewVm): ActionMap {
    return {
      "go-cardgame": () => vm.go("/workshops/values"),
      "go-sektion": () => vm.go("/workshops/values/sektion"),
      plan: () => void vm.plan(),
      "retry-block": ({ id }) => {
        if (id) void vm.retryBlock(id)
      },
      "pick-option": ({ role, id, block }, event) => {
        if (!role || !id || !block) return
        // A second click on the already-chosen option is the fast-path CONFIRM (operator ruling).
        if (role === "choice" && vm.selFor(block).choiceId === id && event) {
          void this.dispatchAnswer(vm, block, event)
          return
        }
        vm.pickOption(block, role as "choice" | "best" | "worst", id)
      },
      "toggle-veto": ({ id, block }) => {
        if (id && block) vm.toggleVeto(block, id)
      },
      "submit-answer": ({ id }, event) => {
        if (id && event) void this.dispatchAnswer(vm, id, event)
      },
    }
  }

  /** Enter confirms THIS card once it is answerable (fast path); a textarea keeps Enter for
   *  newlines. preventDefault stops a focused cell's native Enter-click from double-firing. */
  private onCardKeydown(vm: ValuesInterviewVm, event: KeyboardEvent, blockId: string): void {
    if (event.key !== "Enter" || event.shiftKey) return
    if ((event.target as HTMLElement).tagName === "TEXTAREA") return
    const block = vm.allBlocks().find((b) => b.block.id === blockId)
    if (!block?.card) return
    if (!isAnswerable(block.card.primitive, vm.selFor(blockId))) return
    event.preventDefault()
    void this.dispatchAnswer(vm, blockId, event)
  }

  /** DOM reads are scoped to the block's own card (a round shows several at once). */
  private readTextarea(event: Event): string {
    return readOpenText((event.target as HTMLElement).closest(".iv-card"))
  }
  private readSlider(event: Event): number {
    return readDifferentialValue((event.target as HTMLElement).closest(".iv-card"))
  }

  /** Build the typed answer for a block (selection from its per-block state, free-text/slider
   *  read from that block's card in the DOM) and fold it. */
  private dispatchAnswer(vm: ValuesInterviewVm, blockId: string, event: Event): void {
    const card = vm.allBlocks().find((b) => b.block.id === blockId)?.card
    if (!card) return
    const sel = vm.selFor(blockId)
    let answer: Record<string, unknown> | null = null
    switch (card.primitive) {
      case "pairwise":
        answer = sel.choiceId ? { kind: "pairwise", choice: sel.choiceId } : null
        break
      case "best_worst":
        answer = sel.bestId && sel.worstId ? { kind: "best_worst", best: sel.bestId, worst: sel.worstId } : null
        break
      case "vignette":
      case "simulation":
        answer = sel.choiceId
          ? {
              kind: "vignette",
              choice: sel.choiceId,
              ...(sel.vetoes.length > 0 ? { vetoes: sel.vetoes.map((v) => ({ id: v, intensity: "niemals" })) } : {}),
            }
          : null
        break
      case "semantic_differential":
        answer = { kind: "semantic_differential", position: this.readSlider(event) }
        break
      default: {
        const text = this.readTextarea(event)
        answer = text ? { kind: "open_narrative", text } : null
      }
    }
    if (answer) void vm.submit(blockId, answer)
  }
}
